/**
 * Auth Reset Utility
 * NUCLEAR option to fix persistent login issues
 *
 * Use when: User can login in incognito but not regular browser
 * Cause: Stale cookies, localStorage, sessionStorage, or IndexedDB
 */

import { supabase } from './supabase';

/**
 * Complete auth reset - clears EVERYTHING
 * This is the "turn it off and on again" for authentication
 */
export async function forceAuthReset(): Promise<void> {
  console.log('[AuthReset] 🚨 FORCE RESET INITIATED');

  try {
    // Step 1: Sign out from Supabase (clears server-side session AND cookies)
    console.log('[AuthReset] Step 1: Signing out from Supabase...');

    // Use Promise.race with timeout to prevent hanging
    const signOutPromise = supabase.auth.signOut({ scope: 'global' });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Sign out timeout')), 3000)
    );

    try {
      await Promise.race([signOutPromise, timeoutPromise]);
    } catch (err) {
      console.warn('[AuthReset] Sign out timed out or failed, continuing anyway...');
    }

    // Step 2: Clear ALL localStorage (nuclear)
    console.log('[AuthReset] Step 2: Clearing localStorage...');
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log(`[AuthReset]   ✓ Removed: ${key}`);
    });

    // Step 3: Clear ALL sessionStorage
    console.log('[AuthReset] Step 3: Clearing sessionStorage...');
    sessionStorage.clear();

    // Step 3.5: Clear ALL cookies (including secure HTTP-only ones)
    console.log('[AuthReset] Step 3.5: Clearing all cookies...');
    document.cookie.split(";").forEach(cookie => {
      const name = cookie.split("=")[0].trim();
      // Clear for current domain
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      // Also try with domain specified
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
      console.log(`[AuthReset]   ✓ Cleared cookie: ${name}`);
    });

    // Step 4: Clear IndexedDB (Supabase may use this)
    console.log('[AuthReset] Step 4: Clearing IndexedDB...');
    try {
      const databases = await indexedDB.databases?.();
      if (databases) {
        for (const db of databases) {
          if (db.name) {
            indexedDB.deleteDatabase(db.name);
            console.log(`[AuthReset]   ✓ Deleted database: ${db.name}`);
          }
        }
      }
    } catch (idbError) {
      console.warn('[AuthReset] IndexedDB cleanup failed (may not be supported):', idbError);
    }

    // Step 5: Clear service worker cache (if any)
    console.log('[AuthReset] Step 5: Clearing service worker cache...');
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const name of cacheNames) {
        await caches.delete(name);
        console.log(`[AuthReset]   ✓ Deleted cache: ${name}`);
      }
    }

    console.log('[AuthReset] ✅ COMPLETE - All auth data cleared');
    console.log('[AuthReset] 🔄 Forcing hard reload to /login...');

    // Step 6: Force HARD reload to clean state (bypass cache)
    // Use replace instead of href to prevent back button issues
    window.location.replace('/login');

  } catch (error) {
    console.error('[AuthReset] ❌ Error during reset:', error);
    // Even if there's an error, force reload immediately
    window.location.replace('/login');
  }
}

/**
 * Quick auth cleanup (less nuclear, but thorough)
 * Clears Supabase and Hover data only
 */
export function quickAuthCleanup(): void {
  console.log('[AuthReset] 🧹 Quick cleanup...');

  // Clear Supabase keys (sb-*)
  const supabaseKeys = Object.keys(localStorage).filter(k => k.startsWith('sb-'));
  supabaseKeys.forEach(key => {
    localStorage.removeItem(key);
    console.log(`[AuthReset]   ✓ Removed: ${key}`);
  });

  // Clear Hover keys
  const hoverKeys = Object.keys(localStorage).filter(k =>
    k.includes('hover_') ||
    k.includes('beta_') ||
    k.includes('redheli')
  );
  hoverKeys.forEach(key => {
    localStorage.removeItem(key);
    console.log(`[AuthReset]   ✓ Removed: ${key}`);
  });

  // Clear sessionStorage
  sessionStorage.clear();

  console.log('[AuthReset] ✅ Quick cleanup complete');
}

/**
 * Check if auth storage appears corrupted
 * Returns true if cleanup is recommended
 */
export function detectAuthIssues(): {
  hasIssues: boolean;
  issues: string[];
} {
  const issues: string[] = [];

  try {
    // Check 1: Conflicting auth states
    const supabaseKeys = Object.keys(localStorage).filter(k => k.startsWith('sb-'));
    const hasSupabaseSession = supabaseKeys.length > 0;
    const hasBetaAuth = localStorage.getItem('beta_authenticated') === 'true';

    if (hasSupabaseSession && hasBetaAuth) {
      issues.push('Conflicting Supabase + Beta auth states');
    }

    // Check 2: Malformed session hint
    const hintJson = localStorage.getItem('hover_session_hint');
    if (hintJson) {
      try {
        const hint = JSON.parse(hintJson);
        if (!hint.user_id || !hint.expires_at) {
          issues.push('Malformed session hint');
        }

        // Check if expired
        const expiresAt = new Date(hint.expires_at);
        if (expiresAt <= new Date()) {
          issues.push('Expired session hint not cleaned up');
        }
      } catch {
        issues.push('Unparseable session hint');
      }
    }

    // Check 3: Orphaned Supabase data
    if (supabaseKeys.length > 0 && !hintJson) {
      issues.push('Orphaned Supabase keys without session hint');
    }

    // Check 4: Too many localStorage keys (sign of accumulation)
    const totalKeys = Object.keys(localStorage).length;
    if (totalKeys > 20) {
      issues.push(`Excessive localStorage keys (${totalKeys})`);
    }

    return {
      hasIssues: issues.length > 0,
      issues
    };

  } catch (error) {
    return {
      hasIssues: true,
      issues: ['Error checking auth state']
    };
  }
}

/**
 * Comprehensive auth diagnostic - prints everything to console
 * Call from console: window.hoverDebug.authDiag()
 */
export function authDiagnostic(): void {
  console.log('═══════════════════════════════════════════════════════');
  console.log('🔍 HOVER AUTH DIAGNOSTIC');
  console.log('═══════════════════════════════════════════════════════');
  console.log('Time:', new Date().toISOString());
  console.log('URL:', window.location.href);
  console.log('');

  // 1. LocalStorage dump
  console.log('📦 localStorage:');
  const allKeys = Object.keys(localStorage);
  console.log(`  Total keys: ${allKeys.length}`);

  // Group by type
  const supabaseKeys = allKeys.filter(k => k.startsWith('sb-'));
  const hoverKeys = allKeys.filter(k => k.includes('hover_'));
  const betaKeys = allKeys.filter(k => k.includes('beta_'));
  const pkceKeys = allKeys.filter(k => k.includes('code') || k.includes('verifier') || k.includes('pkce'));
  const otherKeys = allKeys.filter(k =>
    !k.startsWith('sb-') &&
    !k.includes('hover_') &&
    !k.includes('beta_') &&
    !k.includes('code') &&
    !k.includes('verifier') &&
    !k.includes('pkce')
  );

  console.log(`  Supabase keys (${supabaseKeys.length}):`, supabaseKeys);
  supabaseKeys.forEach(key => {
    const val = localStorage.getItem(key);
    console.log(`    - ${key}: ${val ? `${val.substring(0, 50)}...` : 'null'}`);
  });

  console.log(`  PKCE-related keys (${pkceKeys.length}):`, pkceKeys);
  pkceKeys.forEach(key => {
    const val = localStorage.getItem(key);
    console.log(`    - ${key}: ${val ? `${val.substring(0, 50)}...` : 'null'}`);
  });

  console.log(`  Hover keys (${hoverKeys.length}):`, hoverKeys);
  hoverKeys.forEach(key => {
    const val = localStorage.getItem(key);
    console.log(`    - ${key}: ${val ? `${val.substring(0, 50)}...` : 'null'}`);
  });

  console.log(`  Beta keys (${betaKeys.length}):`, betaKeys);
  console.log(`  Other keys (${otherKeys.length}):`, otherKeys);
  console.log('');

  // 2. SessionStorage dump
  console.log('📦 sessionStorage:');
  console.log(`  Total keys: ${Object.keys(sessionStorage).length}`);
  console.log('');

  // 3. Cookies
  console.log('🍪 Cookies:');
  const cookies = document.cookie.split(';').map(c => c.trim());
  console.log(`  Total cookies: ${cookies.length}`);
  cookies.forEach(cookie => {
    const [name, value] = cookie.split('=');
    console.log(`    - ${name}: ${value?.substring(0, 50)}...`);
  });
  console.log('');

  // 4. Auth issues
  const { hasIssues, issues } = detectAuthIssues();
  console.log('⚠️  Detected issues:');
  if (hasIssues) {
    issues.forEach(issue => console.log(`    - ${issue}`));
  } else {
    console.log('    ✅ No issues detected');
  }
  console.log('');

  console.log('═══════════════════════════════════════════════════════');
  console.log('💡 Available commands:');
  console.log('  window.hoverDebug.clearAuth()  - Nuclear reset (clears everything)');
  console.log('  window.hoverDebug.quickClean() - Quick cleanup (Supabase + Hover only)');
  console.log('  window.hoverDebug.authDiag()   - Run this diagnostic again');
  console.log('  window.hoverDebug.watchStorage() - Monitor localStorage changes');
  console.log('═══════════════════════════════════════════════════════');
}

/**
 * Monitor localStorage changes (helps debug PKCE disappearance)
 * Call from console: window.hoverDebug.watchStorage()
 */
export function watchLocalStorage(): void {
  console.log('[StorageWatcher] 👀 Monitoring localStorage changes...');

  // Take snapshot of current state
  const snapshot = new Map<string, string | null>();
  Object.keys(localStorage).forEach(key => {
    snapshot.set(key, localStorage.getItem(key));
  });

  // Poll every 100ms
  const interval = setInterval(() => {
    const currentKeys = Object.keys(localStorage);

    // Check for removed keys
    snapshot.forEach((value, key) => {
      if (!currentKeys.includes(key)) {
        console.error(`[StorageWatcher] ❌ KEY REMOVED: ${key}`);
        console.error(`[StorageWatcher]   Previous value: ${value?.substring(0, 50)}...`);
        console.trace(); // Print stack trace to see who removed it
        snapshot.delete(key);
      }
    });

    // Check for added or modified keys
    currentKeys.forEach(key => {
      const currentValue = localStorage.getItem(key);
      const previousValue = snapshot.get(key);

      if (!snapshot.has(key)) {
        console.log(`[StorageWatcher] ✅ KEY ADDED: ${key}`);
        console.log(`[StorageWatcher]   Value: ${currentValue?.substring(0, 50)}...`);
        snapshot.set(key, currentValue);
      } else if (currentValue !== previousValue) {
        console.log(`[StorageWatcher] 🔄 KEY MODIFIED: ${key}`);
        console.log(`[StorageWatcher]   Old: ${previousValue?.substring(0, 50)}...`);
        console.log(`[StorageWatcher]   New: ${currentValue?.substring(0, 50)}...`);
        snapshot.set(key, currentValue);
      }
    });
  }, 100);

  console.log('[StorageWatcher] Watching... (check console for changes)');
  console.log('[StorageWatcher] To stop: clearInterval(' + interval + ')');

  // Store interval ID globally so user can stop it
  (window as any).hoverDebugWatcherId = interval;
}
